import type { FC, ReactNode } from 'react';
import { useSyncExternalStore } from 'react';

import { Toast } from './Toast';

type ToastLevel = 'success' | 'info' | 'warning' | 'error';

type ToastData = {
  id: string;
  message: string;
  level: ToastLevel;
  autoClose: boolean | undefined;
  autoCloseDelay: number | undefined;
};

type Props = {
  children: ReactNode;
};

const toastStore = () => {
  let toasts: ToastData[] = [];
  let listeners: (() => void)[] = [];

  const addToast = (
    message: string,
    level: ToastLevel,
    options?: { autoClose?: boolean; autoCloseDelay?: number },
  ) => {
    const id = Math.random().toString(36).substring(2, 9);
    toasts = [
      ...toasts,
      {
        id,
        message,
        level,
        autoClose: options?.autoClose,
        autoCloseDelay: options?.autoCloseDelay,
      },
    ];
    listeners.forEach((listener) => listener());
  };

  const removeToast = (id: string) => {
    toasts = toasts.filter((toast) => toast.id !== id);
    listeners.forEach((listener) => listener());
  };

  const subscribe = (listener: () => void) => {
    listeners.push(listener);
    return () => {
      listeners = listeners.filter((l) => l !== listener);
    };
  };

  const getToasts = () => toasts;

  return { addToast, removeToast, subscribe, getToasts };
};

const store = toastStore();

const useToasts = () => {
  return useSyncExternalStore(store.subscribe, store.getToasts);
};

export const ToastProvider: FC<Props> = ({ children }) => {
  const toasts = useToasts();

  return (
    <>
      {children}
      <div className="fixed top-4 right-4 z-[60] flex flex-col gap-2">
        {toasts.map((toast) => (
          <Toast
            key={toast.id}
            id={toast.id}
            message={toast.message}
            level={toast.level}
            onClose={() => store.removeToast(toast.id)}
            autoClose={toast.autoClose}
            autoCloseDelay={toast.autoCloseDelay}
          />
        ))}
      </div>
    </>
  );
};

export const addToast = (
  message: string,
  level: ToastLevel,
  options?: { autoClose?: boolean; autoCloseDelay?: number },
) => {
  store.addToast(message, level, options);
};
